<script setup lang="ts">
import type { Product } from "~/utils/Product"

defineProps<{ product: Product }>()
</script>

<template>
  <NuxtLink :to="`/shop/product/${product.slug}`" class="search-result-card">
    <div class="search-result-card__image">
      <NuxtImg
        v-if="product.images[0]?.rel_url"
        provider="strapi"
        format="webp"
        :src="product.images[0].rel_url"
        :alt="product.images[0].alt || product.Name"
        :class="{ imageCover: !product.transparentImage }"
        loading="lazy"
      />
      <i v-else class="pi pi-image" aria-hidden="true"></i>
    </div>

    <div class="search-result-card__content">
      <div class="search-result-card__meta">
        <span v-if="product.categories?.[0]">{{ product.categories[0].name }}</span>
        <span v-for="badge in product.badges?.slice(0, 2)" :key="badge.label">{{ badge.label }}</span>
      </div>
      <h3>{{ product.Name }}</h3>
      <div class="search-result-card__footer">
        <strong v-if="product.Price">{{ product.Price.toLocaleString("de-AT", { style: "currency", currency: "EUR" }) }}</strong>
        <strong v-else>Preis auf Anfrage</strong>
        <span>Details <i class="pi pi-arrow-right" aria-hidden="true"></i></span>
      </div>
    </div>
  </NuxtLink>
</template>

<style scoped lang="scss">
.search-result-card{
  min-width: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border: 1px solid $public-border;
  border-radius: $public-radius-card;
  color: white;
  background: #191a1d;
  text-decoration: none;
  transition: border-color 180ms $public-ease, background-color 180ms $public-ease;

  &:hover{
    border-color: rgba(213, 150, 69, 0.42);
    background: #1c1d20;
  }

  &:focus-visible{
    border-color: #E39737;
    outline: 2px solid #E39737;
    outline-offset: 3px;
  }
}

.search-result-card__image{
  height: 185px;
  overflow: hidden;
  display: grid;
  place-items: center;
  color: rgb(120 117 115);
  background: #252629;
  font-size: 1.8rem;

  img{
    width: 100%;
    height: 100%;
    padding: 0.85rem;
    object-fit: contain;
    background: $imgBackground;

    &.imageCover{
      padding: 0;
      object-fit: cover;
      background: none;
    }
  }
}

.search-result-card__content{
  flex: 1;
  padding: 1.05rem;
  display: flex;
  flex-direction: column;

  h3{
    margin: 0.7rem 0 1.35rem;
    font-size: 1.03rem;
    font-weight: 600;
    line-height: 1.32;
  }
}

.search-result-card__meta{
  min-height: 1.35rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;

  span{
    max-width: 100%;
    overflow: hidden;
    padding: 0.22rem 0.45rem;
    border-radius: 999px;
    color: rgb(191 188 185);
    background: rgba(255, 255, 255, 0.045);
    font-size: 0.64rem;
    font-weight: 650;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.search-result-card__footer{
  display: flex;
  gap: 0.8rem;
  align-items: flex-end;
  justify-content: space-between;
  margin-top: auto;

  strong{
    font-size: 0.92rem;
  }

  span{
    display: inline-flex;
    gap: 0.4rem;
    align-items: center;
    color: $public-accent;
    font-size: 0.75rem;
    font-weight: 700;
  }
}

@media (max-width: 639px){
  .search-result-card{
    min-height: 124px;
    display: grid;
    grid-template-columns: 112px minmax(0, 1fr);
  }

  .search-result-card__image{
    height: 100%;
    min-height: 124px;
  }

  .search-result-card__content{
    padding: 0.85rem;

    h3{
      margin: 0.5rem 0 0.8rem;
      font-size: 0.94rem;
    }
  }

  .search-result-card__meta span:not(:first-child){
    display: none;
  }

  .search-result-card__footer strong{
    font-size: 0.8rem;
  }
}
</style>
